import { ProductSlimResponseDTO } from "@shared/DTO";
import { getLensMountAffiliates } from "@/scripts/utils/lens-mount-affiliates.utils";
import { getDirectPurchaseCta, getMountPurchaseCta } from "@/scripts/components/purchase-cta/purchaseCta.model";
import {
  LensMountPurchaseOption,
  ProductListingItem,
} from "../abProducts.types";
import {
  ProductCardViewModel,
  PurchaseCtaModel,
} from "./purchaseCta.types";

/**
 * Builds the purchase-aware representation used throughout the listing.
 * The backend has already applied the source's editorial mount scope.
 */
export const createProductListingItem = (
  product: ProductSlimResponseDTO,
): ProductListingItem => {
  if (!product.lensData?.mounts?.length) {
    return {
      product,
      purchaseOptions: { type: "default", url: product.affiliate?.at(0)?.url },
    };
  }

  const options = getLensMountAffiliates(product)
    .map(({ mount, affiliate }): LensMountPurchaseOption => ({
      mount,
      url: affiliate?.url,
    }));

  return {
    product,
    purchaseOptions: { type: "lensMounts", options },
  };
};

export const createProductListingItems = (
  products: ReadonlyArray<ProductSlimResponseDTO>,
): ProductListingItem[] => products.map(createProductListingItem);

export const createProductCardViewModel = (
  item: ProductListingItem,
): ProductCardViewModel => ({
  product: item.product,
  purchaseCta: getPurchaseCta(item),
  ...(item.purchaseOptions.type === "lensMounts" ? {
    purchaseScope: { lensMounts: item.purchaseOptions.options.map(({ mount }) => mount.public_id) },
  } : {}),
});

export const createProductCardViewModels = (
  items: ReadonlyArray<ProductListingItem>,
): ProductCardViewModel[] => items.map(createProductCardViewModel);

export const createStandaloneProductCardViewModel = (
  product: ProductSlimResponseDTO,
): ProductCardViewModel => createProductCardViewModel(
  createProductListingItem(product),
);

const getPurchaseCta = ({ purchaseOptions }: ProductListingItem): PurchaseCtaModel => {
  if (purchaseOptions.type === "default") {
    return getDirectPurchaseCta(purchaseOptions.url);
  }

  return getMountPurchaseCta(purchaseOptions.options);
};
